import type { Event, EventHint } from '@sentry/browser';

/** Vendor stack frames include this substring; it also appears inside chunk names like `vendors~react-monaco-editor.*.js`. */
const MONACO_VENDOR_SUBSTRING = 'monaco-editor';

/**
 * Fetch/Apollo cancels in-flight GraphQL requests via AbortController when the user navigates away,
 * a component unmounts, or the observable is torn down. The rejection is often reported as an
 * unhandled promise rejection even though cancellation is expected.
 */
function isBenignCanceledRequest(exc: { type?: string; value?: string }): boolean {
  if (exc.type === 'AbortError') {
    return true;
  }
  const value = exc.value ?? '';
  if (/signal is aborted/i.test(value)) {
    return true;
  }
  // Legacy / other browsers (e.g. "The user aborted a request.")
  if (/user aborted a request/i.test(value)) {
    return true;
  }
  return false;
}

function filenameLooksLikeMonacoVendor(filename: string | undefined): boolean {
  return Boolean(filename?.includes(MONACO_VENDOR_SUBSTRING));
}

/**
 * Sentry `beforeSend` hook: drop benign cancelled-request noise, yaml.worker noise, and group Monaco-related errors.
 * Also splits ApolloErrors by root cause so each class gets its own Sentry issue.
 */
export function sentryBeforeSend(event: Event, hint?: EventHint): Event | null {
  /* eslint-disable no-param-reassign */
  const exceptions = event.exception?.values ?? [];
  if (exceptions.some(isBenignCanceledRequest)) {
    return null;
  }
  const hasYamlWorker = exceptions.some(exc =>
    (exc.stacktrace?.frames ?? []).some(frame => frame.filename?.includes('yaml.worker.js')),
  );
  if (hasYamlWorker) {
    return null;
  }
  const excWithMonaco = exceptions.find(exc =>
    (exc.stacktrace?.frames ?? []).some(frame => filenameLooksLikeMonacoVendor(frame.filename)),
  );
  if (excWithMonaco) {
    event.fingerprint = [
      '{{ default }}',
      String(excWithMonaco.type ?? 'MonacoError'),
      String(excWithMonaco.value ?? ''),
    ];
  }
  // ApolloErrors all share the same stack regardless of what went wrong, so Sentry buckets
  // HTTP failures, "not found" responses, Snowflake errors, etc. into one issue by default.
  // hint.originalException holds the raw ApolloError before serialization — use it to
  // fingerprint by the actual root cause rather than parsing the serialized message string.
  const hasApolloError = exceptions.some(exc => exc.type === 'ApolloError');
  if (hasApolloError) {
    const orig = hint?.originalException;
    if (orig != null && typeof orig === 'object') {
      const { networkError } = orig as { networkError?: { statusCode?: number } | null };
      const networkStatusCode = networkError?.statusCode;
      if (networkStatusCode != null) {
        // HTTP-level failure: each status code gets its own issue.
        event.fingerprint = ['ApolloError', 'network-error', String(networkStatusCode)];
      } else if (networkError != null) {
        // Network failure with no HTTP response (offline, blocked request, CORS, timeout).
        event.fingerprint = ['ApolloError', 'network-error', 'no-response'];
      } else {
        // GraphQL-level failure: fingerprint by the first error message.
        // Stable messages like "scheduled prompt not found" or "access denied" each get their
        // own issue. Dynamic messages (e.g. Snowflake errors containing query IDs) will create
        // a separate issue per unique message — adding extensions.code to resolvers would give
        // a stable code to use instead.
        const graphqlMessage = (orig as { graphQLErrors?: Array<{ message?: string }> })
          .graphQLErrors?.[0]?.message;
        if (graphqlMessage) {
          event.fingerprint = ['ApolloError', 'graphql-error', graphqlMessage];
        }
      }
    }
  }

  /* eslint-enable no-param-reassign */
  return event;
}
